/**
 * description: enums
 */

// 任务状态：0-未启用；1-启用；
export const enum QuestionStatus {
	NOT_START = 0, // 未启用
	START = 1, // 启用
}
export type QuestionStatusType = keyof typeof QuestionStatus
export type QuestionStatusValue = typeof QuestionStatus[QuestionStatusType]

export enum QuestionType {
	radio = '单选', // 单选
	checkbox = '多选', // 多选
	fillin = '填写框', // 填写框
	file = '上传文件', // 上传文件
	date = '选择日期', // 选择日期
}
export type QuestionTypeType = keyof typeof QuestionType
export type QuestionTypeValue = typeof QuestionType[QuestionTypeType]

// 依赖关系
export const enum RelyType {
	AND = 'and',
	OR = 'or',
}
export type RelyTypeType = keyof typeof RelyType
export type RelyTypeValue = typeof RelyType[RelyTypeType]

// surveyType
export enum SurveyType {
	term_start = '期初问卷',
	term_end = '期末问卷',
	evaluation_question = '评估问卷',
	ai_evaluation_question = 'AI评估问卷',
}
export type SurveyTypeType = keyof typeof SurveyType
export type SurveyTypeValue = typeof SurveyType[SurveyTypeType]

// survey mode
export const enum SurveyMode {
	//answer
	ANSWER = 'answer',
	//preview
	PREVIEW = 'preview',
	//view
	VIEW = 'view',
}
export type SurveyModeType = keyof typeof SurveyMode
export type SurveyModeValue = typeof SurveyMode[SurveyModeType]

/**
 * description: types
 */
// * 问卷实体
export type Survey = {
	surveyName: string // 调查问卷名称
	surveyType: SurveyTypeType // 调查问卷类型
	commentText: string // 备注
	questions: Question[] // 问题列表
	surveyId: IdType // 调查问卷id
	status: QuestionStatusValue // 开启状态 0：关闭 1：启用
	costPoint: number // 花费积分 0：不花费 >0：花费 240501
	createUser: number // 创建人
	createUserName: string // 创建人名称
	createTime: string // 创建时间
	updateUser: number // 更新人
	updateTime: string // 更新时间
}

// Number1to10 就代表了从 1 到 10 的整数类型。
export type Number1to10 = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10

export type Question = {
	ifDecide: boolean // 是否决策
	decideQuestions: Rely[] //决策问题
	ifRely: boolean // 是否依赖
	relyQuestions: Rely[] //依赖问题
	questionId: IdType // 问题id
	items: OptionItem[] //选项
	questionText: string // 问题内容,最大长度为600
	questionType: QuestionTypeType // 问题类型
	sortNo: number // 真实序号 同一个调查问卷下 从1开始
	//20240501新增
	stageLevel: Number1to10 // 阶段等级
	dimension: string // 问题维度
	exampleRichContent: string // 问题示例富文本
}

export type AiQuestionLine = {
	questionLineId?: IdType
	questionDescription: string
	sortNo: number
}

export type AiOptionConfig = 'ability-status' | 'pass-status'

export type AiAbilityPoint = {
	abilityPointId?: IdType
	abilityPointName: string
	stageLevel: Number1to10
	dimension: string
	dimensionName?: string
	abilityPointExplanation: string
	judgmentBasis: string
	optionConfig: AiOptionConfig
	questionDescriptions: AiQuestionLine[]
	exampleRichContent: string
	sortNo: number
}

export type AiSurvey = Omit<Survey, 'questions'> & {
	abilityPoints: AiAbilityPoint[]
}

export type AiQuestionAnswer = {
	questionLineId?: IdType
	sortNo: number
	questionDescription: string
	answerText: string
}

export type AiAbilityAnswer = {
	abilityPointId?: IdType
	sortNo: number
	abilityPointName: string
	stageLevel: Number1to10
	dimension: string
	dimensionName?: string
	optionConfig?: AiOptionConfig
	judgementValue?: string
	questionAnswers: AiQuestionAnswer[]
}

export type AiSurveyDraft = {
	surveyId: IdType
	surveyType: 'ai_evaluation_question'
	clientSubmissionId?: string
	abilityAnswers: AiAbilityAnswer[]
	updatedAt: string
}

export type AiEvaluationStatus = 'pending' | 'processing' | 'retrying' | 'completed' | 'failed'

export type AiEvaluationSubmitResult = {
	recordId: IdType
	evaluationStatus: AiEvaluationStatus
	submittedAt?: string
	pollAfterSeconds?: number
}

export type AiEvaluationRadarItem = {
	dimensionKey: string
	dimensionName: string
	scoreRate: number
	requirementRate?: number
}

export type AiEvaluationRadarStandard = {
	dimensionKey: string
	dimensionCode: string
	dimensionName: string
	kindergartenScore: number
	primarySchoolScore: number
}

export type AiDimensionSummary = {
	dimensionKey: string
	dimensionName: string
	stageLevel: Number1to10
	abilityPointCount?: number
	standardScore?: number
	currentScore?: number
	scoreRate?: number
	scoreLabel: string
	currentStatus: string
	nextSuggestion: string
}

export type AiCapabilityStatus = 'capable' | 'partial' | 'missing'

export type AiQuestionSnapshot = {
	abilityPointId?: IdType
	questionLineId?: IdType
	sortNo?: number
	abilityPointName: string
	stageLevel?: Number1to10
	dimensionKey?: string
	dimensionName?: string
	questionDescription: string
	judgementValue?: string
	answerText: string
	capabilityStatus?: AiCapabilityStatus
	capabilityLabel?: string
	aiConclusion?: string
}

export type AiAbilitySnapshot = {
	abilityPointId?: IdType
	sortNo?: number
	abilityPointName: string
	stageLevel?: Number1to10
	dimensionKey?: string
	dimensionName?: string
	optionConfig?: AiOptionConfig
	judgementValue?: string
	score: 0 | 1 | 2
	capabilityStatus: AiCapabilityStatus
	capabilityLabel: string
	aiConclusion?: string
	questionSnapshots: AiQuestionSnapshot[]
}

export type AiEvaluationResultSnapshot = {
	schemaVersion?: number
	overallSummary: string
	kindergartenRadar: AiEvaluationRadarItem[]
	primarySchoolRadar: AiEvaluationRadarItem[]
	dimensionSummaries: AiDimensionSummary[]
	abilitySnapshots?: AiAbilitySnapshot[]
	questionSnapshots?: AiQuestionSnapshot[]
}

export type AiEvaluationRecordItem = {
	recordId: IdType
	learnerId: IdType
	surveyId: IdType
	nthEvaluateNo: number
	progressPercent: number
	answeredCount: number
	totalCount: number
	questionSummary: string
	evaluateTime: string
	evaluatorName: string
	learnerStage: string
	submitStatus?: AiEvaluationStatus
	evaluationStatus: AiEvaluationStatus
	submittedAt?: string
	completedAt?: string
	attemptCount?: number
	errorCode?: string
	errorSummary?: string
	canRetry?: boolean
}

export type AiEvaluationRecordDetail = AiEvaluationRecordItem & {
	learnerName: string
	learnerHeadPic?: string
	surveyName?: string
	resultSnapshot: AiEvaluationResultSnapshot | null
	radarStandards?: AiEvaluationRadarStandard[]
	abilityAnswers: AiAbilityAnswer[]
}
export type OptionItem = {
	ifShowBlank: boolean //是否展示填空
	value: string //选项的值
	example?: string // 选项的示例，只有单选才有
	appIconUrl?: string // 期末问卷的单选中，每个选项右侧的“展示填空”字段调整为“展示图标”字段
}

export type Rely = {
	questionId: Question['questionId'] // 问题id
	conditionItems: string // 条件选项(多选时 SPLIT_SYMBOL 分割）,最大长度为600 ，** 现阶段只会是单选的情况 **
	relyType: RelyTypeValue // 依赖关系(or 或者and),最大长度为20
}

export const SPLIT_SYMBOL = '&'
export const getRelyItemsString = (val: string[]) => val.join(SPLIT_SYMBOL)
export const getRelyItemsArray = (val: string) => (val && typeof val === 'string' ? val.split(SPLIT_SYMBOL) : [])

// * 提交问卷答案
//期初
export type SurveyReq = {
	surveyId: Survey['surveyId']
	questionFillInReqs: QuestionFillInReq[]
}

//期末
export type SurveyReqOfTermEnd = SurveyReq & { fillinId: IdType }

//评估：首次提交时后端会按当前登录用户复用或创建 DRAFT DataLearner，因而 fillinId 可省略；
//已有评估记录的再次提交仍携带 fillinId。
export type SurveyReqOfEvaluation = SurveyReq & { fillinId?: IdType }

export type FormDataWithSurveyAnswer = Record<Question['questionId'], QuestionFillInReq> //答案对应的form data

export type QuestionFillInReq = QuestionFillInReqOfPart & Pick<Question, 'questionId' | 'questionType' | 'dimension' | 'exampleRichContent' | 'stageLevel'> //问题类型：单选：radio;多选：checkbox;填写框：fillin;上传文件：file;选择日期：date

export type QuestionFillInReqOfPart = {
	fileUrl: string[] // 附件地址
	fillInContent: string // 用户填写内容：适用输入框问题类型
	fillinDate: string // 日期（时间） 问题类型 所填写的日期值
	moreInfo: string // 用户填写内容：填写的补充说明信息
	selectItems: SelectItem[] // 	用户填写内容：单选、多选这两种问题类型 所填内容 单选时 数组元素是一个；多选时 数组元素是多个
}

export type SelectItem = {
	content: string // 选项内容
	contentMemo: string // 选项补充说明
}

export const SURVEY_KEY_H5 = 'surveyKeyH5'
export const AI_SURVEY_DRAFT_KEY_H5 = 'aiSurveyDraftKeyH5'
export type SurveyDetailsAnswer = {
	surveyDetails: Survey
	surveyAnswers: FormDataWithSurveyAnswer
}
export type SurveyStorage = SurveyDetailsAnswer & {
	mode: SurveyMode.PREVIEW | SurveyMode.VIEW
}

/**
 * description: utils
 */
export const isCheckbox = (questionType: QuestionTypeType) => questionType === 'checkbox'

export const newEmptySelectItem = (): SelectItem => ({ content: '', contentMemo: '' })
// 根据问题初始化一条对应的空问题
export const initEmptyQuestion = ({ questionId, questionType, items, dimension, stageLevel, exampleRichContent }: Question): QuestionFillInReq => {
	const selectItems = questionType === 'radio' ? [newEmptySelectItem()] : questionType === 'checkbox' ? items.map(() => newEmptySelectItem()) : []
	return { questionId, questionType, fileUrl: [], fillInContent: '', fillinDate: '', selectItems, moreInfo: '', dimension, stageLevel, exampleRichContent }
}

// init empty formData by surveyDetails
export const initEmptyFormData = (sur: Survey): Record<Question['questionId'], QuestionFillInReq> => {
	const { questions } = sur
	const formData: FormDataWithSurveyAnswer = {}
	questions.forEach(q => {
		formData[q.questionId] = initEmptyQuestion(q)
	})

	return formData
}

export const getSurveyStorage = (): SurveyStorage => {
	const surveyStorage = sessionStorage.getItem(SURVEY_KEY_H5)
	return surveyStorage ? JSON.parse(surveyStorage) : {}
}

export const setSurveyStorage = (sur: SurveyStorage) => {
	sessionStorage.setItem(SURVEY_KEY_H5, JSON.stringify(sur))
}

export const removeSurveyStorage = () => {
	sessionStorage.removeItem(SURVEY_KEY_H5)
}

export const getAiSurveyDraftStorageKey = (surveyId: IdType, userId = '', learnerId = '', fillinId = '') =>
	[AI_SURVEY_DRAFT_KEY_H5, userId || 'anonymous', learnerId || 'unknown', surveyId, fillinId || 'new'].join(':')

export const getAiSurveyDraft = (storageKey: string): AiSurveyDraft | null => {
	try {
		const surveyDraft = localStorage.getItem(storageKey)
		return surveyDraft ? JSON.parse(surveyDraft) : null
	} catch (error) {
		try {
			localStorage.removeItem(storageKey)
		} catch (removeError) {
			// Storage may be unavailable in private or restricted WebViews.
		}
		return null
	}
}

export const setAiSurveyDraft = (storageKey: string, draft: AiSurveyDraft): boolean => {
	try {
		localStorage.setItem(storageKey, JSON.stringify(draft))
		return true
	} catch (error) {
		return false
	}
}

export const removeAiSurveyDraft = (storageKey: string) => {
	try {
		localStorage.removeItem(storageKey)
	} catch (error) {
		// Removing a draft should not block submission in restricted WebViews.
	}
}

export const createAiEvaluationSubmissionId = () => {
	const randomPart = Math.random().toString(36).slice(2, 12)
	return `ai-${Date.now().toString(36)}-${randomPart}`
}

/**
 * description: 调查问卷已提交的信息
 */
export type QuestionDto = QuestionFillInReq & {
	questionText: string // 问题文本
	sortNo: number // 真实序号
	items: string[] // 单选或者多选时的选项数组 JSON.stringify(OptionItem)[],最大长度为600
} & Pick<Question, 'ifDecide' | 'ifRely' | 'decideQuestions' | 'relyQuestions'>
// * 提交的问卷答案
//期初
export type LearnerSurvey = {
	questionDtos: QuestionDto[]
	surveyId: Survey['surveyId']
	surveyName: Survey['surveyName']
}
//期末
export type LearnerSurveyOfTermEnd = LearnerSurvey & { fillinId: IdType }

//评估
export type LearnerSurveyOfEvaluation = LearnerSurveyOfTermEnd

// get surveyDetails by learnerSurvey
export const getSurveyDetails = (surveyId: LearnerSurvey['surveyId'], surveyName: LearnerSurvey['surveyName'], questionDtos: LearnerSurvey['questionDtos']) => {
	const questions: Question[] = questionDtos.map(q => {
		const {
			questionId,
			questionType,
			sortNo,
			questionText,
			dimension,
			stageLevel,
			exampleRichContent,
			ifDecide = false,
			ifRely = false,
			decideQuestions = [],
			relyQuestions = [],
		} = q
		const items = (q.items ?? []).map(item => JSON.parse(item))
		return {
			questionId,
			questionType,
			questionText,
			items,
			sortNo,
			ifDecide,
			decideQuestions,
			ifRely,
			relyQuestions,
			dimension,
			stageLevel,
			exampleRichContent,
		}
	})

	return { surveyId, surveyName, questions }
}
// get surveyAnswers by questionDtos
export const getSurveyAnswers = (questionDtos: LearnerSurvey['questionDtos']) => {
	const surveyAnswers: FormDataWithSurveyAnswer = {}
	questionDtos.forEach(question => {
		const { questionId, questionType, fileUrl, fillInContent, fillinDate, moreInfo, items, dimension, stageLevel, exampleRichContent } = question
		let selectItems = question.selectItems ?? []
		if (isCheckbox(questionType)) {
			selectItems = items.map(item => {
				const newItem = JSON.parse(item) as OptionItem
				const matchItem = selectItems.find(i => i.content === newItem.value)
				return matchItem ? matchItem : newEmptySelectItem()
			})
		}
		surveyAnswers[question.questionId] = { questionId, questionType, fileUrl, fillInContent, fillinDate, moreInfo, selectItems, dimension, stageLevel, exampleRichContent }
	})

	return surveyAnswers
}

// get surveyDetails and surveyAnswers by learnerSurvey
export const getDetailsAndAnswers = (learnerSurvey: LearnerSurvey) => {
	const { surveyId, surveyName, questionDtos } = learnerSurvey
	questionDtos.sort((q, nq) => q.sortNo - nq.sortNo)
	const surveyDetails = getSurveyDetails(surveyId, surveyName, questionDtos) as unknown as Survey
	const surveyAnswers = getSurveyAnswers(questionDtos)

	return { surveyDetails, surveyAnswers }
}

// 评估问卷选项
export const EVA_OPTIONS = ['具备', '部分具备', '不具备']
export const getEvaQuestionItems = (): OptionItem[] => EVA_OPTIONS.map(option => ({ ifShowBlank: false, value: option }))

export const EVA_STAGE_LEVELS: Number1to10[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

export const QUES_DIMENSIONS = [
	{ dictKey: '1', dictValue: '听懂' },
	{ dictKey: '2', dictValue: '表达' },
	{ dictKey: '3', dictValue: '学习速度' },
	{ dictKey: '4', dictValue: '思维' },
	{ dictKey: '5', dictValue: '社交' },
	{ dictKey: '6', dictValue: '情绪' },
]

// 根据dictKey 获取 QuestionDimension 的 dictValue
export const getQuesDimensionValue = (key: string) => {
	const match = QUES_DIMENSIONS.find(dim => dim.dictKey === key)
	return match?.dictValue ?? ''
}
